"use client";

import { useMemo } from "react";
import sortBy from "lodash/sortBy";
import { RecipeProductTitle } from "libs/api/customer/src/lib/types/products";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";
import { Gender } from "@./cat-calculations";
import { Recipe, RecipesForCatResponse } from "@forms/schema";

import { IRecipeCard } from "./RecipeCard/RecipeCard";
import {
  useNormaliseTranslations,
} from "apps/website/hooks/useNormalisedTranslations";

const BASE_IMAGE_PATH = "/images/features/product-overview/packaging-content/";

interface IUseTransformRecipesToCardsProps {
  recipes: RecipesForCatResponse
  catName: string;
  gender: Gender;
}

type FACTS = "singleProtein" | "fussyCats" | "ironVitaminA" | "fishOil" | "keepsCatFull";

const factsPerRecipe: Record<Recipe | RecipeProductTitle, FACTS[]> = {
  CLUCK: [ "singleProtein", "fussyCats" ],
  SHREDDED_CLUCK: [ "singleProtein", "fussyCats" ],
  GOBBLE: [ "keepsCatFull" ],
  SHREDDED_GOBBLE: [ "keepsCatFull" ],
  MOOO: [ "ironVitaminA", "fishOil" ],
  OINK: [ "singleProtein" ],
  QUACK: [ "fishOil", "keepsCatFull" ],
  SPLASH: [ "fishOil", "keepsCatFull" ],
  BAAA: [ "singleProtein", "fishOil" ],
  SWISH: [ "fishOil", "keepsCatFull" ],
};

export const getRecipeRank = (item: IRecipeCard): number => {
  if (item.quantity === undefined) { return 2; }
  if (item.quantity > 0) { return 0; } // recommended first
  if (item.quantity === 0) { return 2; } // zero-qty second to last
  return 1; // no promotion gets rank 1 (middle)
};

export const useTransformRecipesToCards = (
  { recipes,
    catName,
    gender }: IUseTransformRecipesToCardsProps,
): IRecipeCard[] => {
  const t = useNormaliseTranslations("Forms.Fresh.DirectSales.Steps.Recipes.List", false, true);

  const interpolationVariables = useMemo(() => getInterpolationVariables(
    { name: catName, gender },
  ), [ catName, gender ]);

  const transformedRecipes: IRecipeCard[] = [];

  Object.entries(
    recipes.allowedRecipesAndQuantities,
  ).forEach(([ recipe, quantity ]) => {
    if (quantity === 0) { return; }
    transformedRecipes.push({
      description: t(`${recipe}.description`, interpolationVariables),
      quantity,
      image: {
        src: `${BASE_IMAGE_PATH}${recipe.toLowerCase().replaceAll("_", "-")}.webp`,
        width: 240,
        height: 180,
      },
      titleTag: {
        label: recipe.replaceAll("_", " "),
      },
      imageBadge: {
        label: t(`${recipe}.imageBadge`),
        styleOptions: { bgColor: "brand" as const },
      },
      badgeList: (factsPerRecipe[recipe as Recipe] || [])
        .map((fact) => ({ label: t(`Facts.${fact}`, interpolationVariables), icon: "tickHeavy" as const, styleOptions: { bgColor: "mid-green" as const } })),
      styleOptions: { bgColor: "light-grey" as const, imageScale: 110 },
    });
  });

  return sortBy(transformedRecipes, [
    (recipe) => getRecipeRank(recipe),
    (recipe) => -(recipe.quantity || 0),
  ]);
};
